import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import COUNTRIES from '../data/countries';

/**
 * Searchable country selector with SVG flag icons.
 *
 * Props:
 *  - value: selected ISO2 code (e.g. "gb")
 *  - onChange: (country: { name, iso2, dial }) => void
 */
export default function CountryCodeSelect({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const wrapperRef = useRef(null);
    const searchRef = useRef(null);

    // Selected country matched by ISO2 (unambiguous, unlike shared dial codes).
    const selected = useMemo(
        () => COUNTRIES.find((c) => c.iso2 === value) || COUNTRIES.find((c) => c.iso2 === 'gb'),
        [value]
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return COUNTRIES;
        return COUNTRIES.filter(
            (c) =>
                c.name.toLowerCase().includes(q) ||
                c.dial.replace('+', '').includes(q.replace('+', '')) ||
                c.iso2.includes(q)
        );
    }, [query]);

    // Close on outside click.
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search box when opening.
    useEffect(() => {
        if (open && searchRef.current) searchRef.current.focus();
    }, [open]);

    const handleSelect = (country) => {
        onChange(country);
        setOpen(false);
        setQuery('');
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-2 p-4 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none bg-white whitespace-nowrap h-full"
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={`fi fi-${selected?.iso2} rounded-sm`} style={{ width: 22, height: 16 }} />
                <span className="text-gray-900 font-medium">{selected?.dial}</span>
                <ChevronDown size={16} className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="absolute z-50 mt-2 w-72 bg-white border-2 border-gray-200 rounded-lg shadow-xl overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                        <Search size={16} className="text-gray-400 shrink-0" />
                        <input
                            ref={searchRef}
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search country or code…"
                            className="w-full py-1 text-sm focus:outline-none"
                        />
                    </div>
                    <ul className="max-h-64 overflow-y-auto py-1" role="listbox">
                        {filtered.length === 0 ? (
                            <li className="px-4 py-3 text-sm text-gray-400">No matches</li>
                        ) : (
                            filtered.map((c) => (
                                <li key={c.iso2 + c.dial}>
                                    <button
                                        type="button"
                                        onClick={() => handleSelect(c)}
                                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-orange-50 transition-colors ${
                                            c.iso2 === value ? 'bg-orange-50' : ''
                                        }`}
                                        role="option"
                                        aria-selected={c.iso2 === value}
                                    >
                                        <span className={`fi fi-${c.iso2} rounded-sm shrink-0`} style={{ width: 22, height: 16 }} />
                                        <span className="flex-1 text-gray-900 truncate">{c.name}</span>
                                        <span className="text-gray-500">{c.dial}</span>
                                    </button>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
